<script setup lang="ts">
import { notify } from "@kyvg/vue3-notification";
import { useQuery } from "@vue/apollo-composable";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { PgDataEntryUploadQuery } from "@/__generated__/types";
import useUploadedFileForReview from "@/api/mutations/RepositoryFile/uploadedFileForReview.mutation";
import OgS3FilePicker from "@/components/molecules/MlFilePicker/OgS3FilePicker.vue";
import MlSelect from "@/components/molecules/MlSelect/MlSelect.vue";
import PG_DATA_ENTRY_UPLOAD_QUERY from "./DataSheets.query";

const { t } = useI18n();

const { result } = useQuery<PgDataEntryUploadQuery>(PG_DATA_ENTRY_UPLOAD_QUERY);
const { mutate } = useUploadedFileForReview();

const projects = computed(
	() =>
		result.value?.entityLocationSummaries.reduce<Record<string, string>>(
			(acc, { _id, name }) => ({ ...acc, [_id]: name }),
			{},
		) ?? {},
);

const topics = computed<Record<string, string>>(
	() =>
		result.value?.getCategories
			.flatMap((c) =>
				c.subcategories.map((s) => [s.slug, `${t(c.slug)}: ${t(s.slug)}`]),
			)
			.reduce(
				(acc, curr) => {
					acc[curr[0]] = curr[1];

					return acc;
				},
				{} as Record<string, string>,
			) ?? {},
);

const uploadTargetProjects = ref<string[]>([]);
const uploadTargetTopics = ref<string[]>([]);

const onAfterUpload = async (repositoryFileId?: string | null) => {
	if (!repositoryFileId) {
		return;
	}

	if (!uploadTargetProjects.value.length) {
		notify({
			type: "error",
			text: t("Please select a project."),
		});
		return;
	}

	await mutate({
		repositoryFileId,
		projectIds: uploadTargetProjects.value,
		topics: uploadTargetTopics.value,
	});

	notify({
		type: "success",
		text: t("Your file has been uploaded."),
	});
};
</script>

<template>
  <div>

    <p class="text-sm text-gray-500 mt-3">
      {{ t('Here you can upload Data Sheets for Manual Review: These include spreadsheets containing specific data points that may be used to directly complete certain sections. These will be reviewed manually by our team to ensure accuracy. You will receive a notification from our team within 72 hours.') }}
    </p>


    <div class="grid grid-cols-3 gap-5 mt-24 mb-10">

      <div class="bg-blue-50 p-10">

        <p class="text-gray-400">{{t('The current upload should be used for the following:')}}</p>

        <MlSelect
            :options="topics"
            :sorted-options="true"
            :multiple="true"
            v-model="uploadTargetTopics"
            wrapperClass="mt-5"
            :placeholder="t('All topics')"
        />

        <MlSelect
            :options="projects"
            :sorted-options="true"
            :multiple="true"
            v-model="uploadTargetProjects"
            wrapperClass="mt-5"
            :placeholder="t('All projects')"
        />


      </div>

      <div class="bg-blue-50 p-10 text-gray-400 col-span-2">
        <OgS3FilePicker class="w-full" @update:modelValue="onAfterUpload($event?._id)" />
      </div>

    </div>

  </div>

</template>
